import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged, createUserWithEmailAndPassword, 
         updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

// Get the auth instance
const auth = getAuth();

/**
 * Sign in with email and password
 * @param {string} email - Admin email
 * @param {string} password - Admin password
 * @returns {Promise<Object>} - Result object with success status
 */
export const adminSignIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return { 
      success: true, 
      user: userCredential.user 
    };
  } catch (error) {
    console.error("Login error:", error);
    let errorMessage = "Unable to sign in";
    
    switch (error.code) {
      case 'auth/invalid-email':
        errorMessage = "Invalid email format";
        break;
      case 'auth/user-disabled':
        errorMessage = "This account has been disabled";
        break;
      case 'auth/user-not-found':
        errorMessage = "Invalid email or password";
        break;
      case 'auth/wrong-password':
        errorMessage = "Invalid email or password";
        break;
      case 'auth/too-many-requests':
        errorMessage = "Too many failed attempts. Please try again later";
        break;
      default:
        errorMessage = "An error occurred during sign in";
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

/**
 * Sign out the current user
 * @param {Object} options - Options for sign out
 * @param {Object} options.navigate - React Router's navigate function
 * @param {string} options.redirectTo - Path to redirect to after signout (defaults to homepage)
 * @returns {Promise<Object>} - Result object with success status
 */
export const adminSignOut = async (options = {}) => {
  try {
    await signOut(auth);
    toast.success("Logged out successfully");
    
    // If navigate function is provided, redirect the user
    if (options.navigate) {
      const redirectPath = options.redirectTo || '/';
      options.navigate(redirectPath);
    }
    
    return { success: true };
  } catch (error) {
    console.error("Logout error:", error);
    return { 
      success: false, 
      error: error.message 
    };
  }
};

/**
 * Check if user is authenticated
 * @param {Function} callback - Callback function that receives auth state
 * @returns {Function} - Unsubscribe function
 */
export const checkAuthState = (callback) => {
  return onAuthStateChanged(auth, (user) => {
    callback(user);
  });
};

/**
 * Get the current user
 * @returns {Object|null} - Current user or null if not authenticated
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};

/**
 * Create a new admin user
 * @param {string} email - New admin email
 * @param {string} password - New admin password
 * @returns {Promise<Object>} - Result object with success status
 */
export const createNewAdmin = async (email, password) => {
  try {
    // Create user with Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Store additional user data in Firestore with default role "admin"
    await setDoc(doc(db, "admins", user.uid), {
      email: email,
      role: "admin", // Setting default role as admin
      createdAt: new Date().toISOString(),
      lastLogin: new Date().toISOString()
    });
    
    return { 
      success: true, 
      user: user 
    };
  } catch (error) {
    console.error("Error creating admin:", error);
    let errorMessage = "Unable to create admin account";
    
    switch (error.code) {
      case 'auth/email-already-in-use':
        errorMessage = "This email is already in use";
        break;
      case 'auth/invalid-email':
        errorMessage = "Invalid email format";
        break;
      case 'auth/weak-password':
        errorMessage = "Password is too weak";
        break;
      case 'auth/operation-not-allowed':
        errorMessage = "Operation not allowed";
        break;
      default:
        errorMessage = "An error occurred while creating the account";
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

/**
 * Update user password
 * @param {string} currentPassword - Current password for reauthentication
 * @param {string} newPassword - New password to set
 * @returns {Promise<Object>} - Result object with success status
 */
export const updateUserPassword = async (currentPassword, newPassword) => {
  try {
    const user = auth.currentUser;
    
    if (!user) {
      return {
        success: false,
        error: "You must be logged in to change your password"
      };
    }

    // Create credential with current password
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );

    // Reauthenticate user
    await reauthenticateWithCredential(user, credential);
    
    // Update the password
    await updatePassword(user, newPassword);
    
    return {
      success: true
    };
  } catch (error) {
    console.error("Password update error:", error);
    let errorMessage = "Failed to update password";
    
    switch (error.code) {
      case 'auth/wrong-password':
        errorMessage = "The current password is incorrect";
        break;
      case 'auth/weak-password':
        errorMessage = "The new password is too weak";
        break;
      case 'auth/requires-recent-login':
        errorMessage = "This operation is sensitive and requires recent authentication. Please log in again.";
        break;
      default:
        errorMessage = "An error occurred while updating your password";
    }
    
    return {
      success: false,
      error: errorMessage
    };
  }
};
